import { useRef, useEffect, useCallback } from 'react';
import type { DialogueTurn } from '@/utils/parseScript';

interface TeleprompterOverlayProps {
  script: string;
  isScrolling: boolean;
  scrollSpeed: number; // 1-10
  fontSize: number;
  // Dialogue-turn mode (Scene Partner)
  dialogueTurns?: DialogueTurn[];
  currentTurnIndex?: number;
  actorCharacter?: string;
}

export function TeleprompterOverlay({
  script,
  isScrolling,
  scrollSpeed,
  fontSize,
  dialogueTurns,
  currentTurnIndex,
  actorCharacter,
}: TeleprompterOverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollYRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const turnRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const isDialogueMode = dialogueTurns && dialogueTurns.length > 0;

  // --- Scroll mode animation ---
  const animate = useCallback(() => {
    if (!containerRef.current || !contentRef.current) return;
    const pixelsPerFrame = scrollSpeed * 0.03;
    scrollYRef.current += pixelsPerFrame;
    const maxScroll = contentRef.current.scrollHeight - containerRef.current.clientHeight;
    if (scrollYRef.current > maxScroll) scrollYRef.current = maxScroll;
    contentRef.current.style.transform = `translateY(-${scrollYRef.current}px)`;
    rafRef.current = requestAnimationFrame(animate);
  }, [scrollSpeed]);

  useEffect(() => {
    if (isDialogueMode) return; // Skip scroll animation in dialogue mode
    if (isScrolling) {
      rafRef.current = requestAnimationFrame(animate);
    } else if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isScrolling, animate, isDialogueMode]);

  // Reset scroll position when script changes
  useEffect(() => {
    if (isDialogueMode) return;
    scrollYRef.current = 0;
    if (contentRef.current) {
      contentRef.current.style.transform = 'translateY(0px)';
    }
  }, [script, isDialogueMode]);

  // --- Dialogue mode: auto-scroll to current turn (also re-scrolls on font size change) ---
  useEffect(() => {
    if (!isDialogueMode || currentTurnIndex === undefined) return;
    const el = turnRefs.current.get(currentTurnIndex);
    if (el && containerRef.current) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentTurnIndex, isDialogueMode, fontSize]);

  if (!isDialogueMode && !script.trim()) return null;

  return (
    <div
      ref={containerRef}
      className="absolute left-4 right-4 overflow-hidden pointer-events-none"
      style={{ top: '0%', height: '50%' }}
    >
      <div className="absolute inset-0 bg-black/40 rounded-lg" />

      {isDialogueMode ? (
        // --- Dialogue turn-by-turn mode ---
        <div className="relative px-4 py-4 space-y-3 overflow-y-auto h-full" style={{ scrollBehavior: 'smooth' }}>
          {dialogueTurns!.map((turn, i) => {
            const isActor = turn.character === actorCharacter;
            const isCurrent = i === currentTurnIndex;
            const isPast = currentTurnIndex !== undefined && i < currentTurnIndex;

            return (
              <div
                key={i}
                ref={(el) => { if (el) turnRefs.current.set(i, el); }}
                className={`rounded-md px-3 py-2 transition-all duration-300 ${
                  isCurrent
                    ? 'bg-white/20 ring-1 ring-white/50'
                    : isPast
                      ? 'opacity-30'
                      : 'opacity-60'
                }`}
              >
                <span
                  className={`text-xs font-bold uppercase tracking-wide ${
                    isActor ? 'text-yellow-300' : 'text-blue-300'
                  }`}
                >
                  {turn.character}
                </span>
                <p
                  className={`mt-0.5 leading-relaxed ${
                    isActor ? 'text-white' : 'text-white/70'
                  }`}
                  style={{ fontSize: `${fontSize}px` }}
                >
                  {turn.line}
                </p>
              </div>
            );
          })}
        </div>
      ) : (
        // --- Standard scroll mode ---
        <div
          ref={contentRef}
          className="relative px-4 py-6 text-white leading-relaxed whitespace-pre-wrap"
          style={{ fontSize: `${fontSize}px` }}
        >
          {/* Reserve space at top-right so the scroll speed pill in CameraPreview
              doesn't cover the first lines of script (critical in portrait/vertical mode). */}
          <div className="float-right w-20 h-8" aria-hidden="true" />
          {script}
        </div>
      )}
    </div>
  );
}
